import { QuestionItemV2 } from '@examdojo/models/question';
import isEqual from 'lodash/isEqual';
import { BehaviorSubject, combineLatest, map, Subject } from 'rxjs';

export class QuestionItemsHistoryManager {
  constructor(private readonly options: { debounceTime: number }) {}

  private lastSaveDate: Date | null = null;
  private readonly history$$ = new BehaviorSubject<QuestionItemV2[][]>([]);

  private readonly currentStateIndex$$ = new BehaviorSubject<number>(0);
  readonly currentState$ = combineLatest([this.history$$, this.currentStateIndex$$]).pipe(
    map(([history, currentStateIndex]) => history[currentStateIndex]),
  );

  readonly navigationEvent$ = new Subject<{ direction: 'back' | 'forward'; state: QuestionItemV2[] }>();

  readonly canGoBack$ = this.currentStateIndex$$.pipe(map((index) => index > 0));
  readonly canGoForward$ = this.currentStateIndex$$.pipe(map((index) => index < this.history$$.value.length - 1));

  pushState(questionItems: QuestionItemV2[], { ignoreEqual = false }: { ignoreEqual?: boolean } = {}) {
    if (ignoreEqual && isEqual(this.history$$.value[this.currentStateIndex$$.value], questionItems)) {
      return;
    }

    if (this.lastSaveDate && Date.now() - this.lastSaveDate.getTime() < this.options.debounceTime) {
      // override the last stored value
      this.history$$.next([...this.history$$.value.slice(0, this.currentStateIndex$$.value), questionItems]);
      this.lastSaveDate = new Date();

      return;
    }

    this.lastSaveDate = new Date();
    this.history$$.next([...this.history$$.value.slice(0, this.currentStateIndex$$.value + 1), questionItems]);
    this.currentStateIndex$$.next(this.history$$.value.length - 1);
  }

  clearHistory() {
    this.history$$.next([]);
    this.currentStateIndex$$.next(0);
  }

  goBack() {
    this.currentStateIndex$$.next(this.currentStateIndex$$.value - 1);
    this.navigationEvent$.next({ direction: 'back', state: this.history$$.value[this.currentStateIndex$$.value] });
  }

  goForward() {
    this.currentStateIndex$$.next(this.currentStateIndex$$.value + 1);
    this.navigationEvent$.next({ direction: 'forward', state: this.history$$.value[this.currentStateIndex$$.value] });
  }
}
